import { useIncomeTypes } from "@/stores/incomeTypes";
import { incomeLabel } from "@/utils/incomeTypes";
import type { ReactElement } from "react";
import { useSelector } from "react-redux";
import { Card, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { ShopOrderReward } from "@/api/shopOrder";
import { formatDateTime } from "@/utils/dateTime";

interface Session {
  roles?: string[];
  permissions?: string[];
  data?: { roles?: string[]; permissions?: string[] };
}
interface SessionState { userInfo: { value: Session } }
interface OrderRewardsTableProps { rewards?: ShopOrderReward[] | null }

const text = (value?: string | null): string => value || "-";
const enumLabel = (labels: Record<string, string>, value?: string | null): string =>
  value == null || value === "" ? "-" : labels[value] ?? `未知（${value}）`;
// 账目可能为积分或不同资金账户，不统一加人民币符号，也不将缺失金额当作0。
const amount = (value?: number | string | null): string =>
  value == null || value === "" || !Number.isFinite(Number(value)) ? "-" : Number(value).toFixed(2);

export function OrderRewardsTable({ rewards }: OrderRewardsTableProps): ReactElement | null {
  const { accountTypes, businessTypes } = useIncomeTypes();
  const columns: ColumnsType<ShopOrderReward> = [
    { title: "入账用户ID", dataIndex: "userId", width: 120 },
    { title: "用户名/运营中心名称", dataIndex: "nickName", render: text, width: 180 },
    { title: "账号", dataIndex: "mobile", render: text, width: 140 },
    { title: "金额", dataIndex: "amount", render: amount, width: 110 },
    { title: "资金走向类型", width: 120, render: (_: unknown, reward: ShopOrderReward): ReactElement =>
      <Tag color={reward.accountTrend === "2" ? "green" : "blue"}>{reward.accountTrendStr || enumLabel({ "1": "收入", "2": "支出" }, reward.accountTrend)}</Tag> },
    { title: "分润类型", width: 140, render: (_: unknown, reward: ShopOrderReward): string => incomeLabel(businessTypes, reward.businessType, reward.businessTypeStr) },
    { title: "资金类型", width: 140, render: (_: unknown, reward: ShopOrderReward): string => incomeLabel(accountTypes, reward.amountType, reward.amountTypeStr) },
    { title: "创建时间", dataIndex: "createTime", render: formatDateTime, width: 180 },
  ];

  const session = useSelector((state: SessionState) => state.userInfo.value);
  const roles = session.roles ?? session.data?.roles ?? [];
  const permissions = session.permissions ?? session.data?.permissions ?? [];
  // 本组件仅注册于总后台；商户身份即使携带同名订单权限也不展示分润数据。
  if (roles.includes("merchant") || !permissions.some((permission) => permission === "*:*:*" || permission === "shop:order:query")) return null;
  return <Card title={`分润详情（${incomeLabel(businessTypes, 37)}）`}>
    <Table<ShopOrderReward> rowKey="id" size="small" columns={columns} dataSource={rewards ?? []}
      locale={{ emptyText: "暂无分润记录" }} scroll={{ x: "max-content" }}
      pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }} />
  </Card>;
}
